class HomeSlideshow extends HTMLElement {
  constructor() {
    super();
    this._viewedBanners = [];
    this._thkRedResizeHandler = null;
    this._idleCallbackId = null;
    this._timeoutId = null;
    this._retryTimeoutId = null;
    this._initScheduled = false;
  }

  connectedCallback() {
    this.scheduleInit();
  }

  disconnectedCallback() {
    this.cancelScheduledInit();
    this.destroy();
  }

  cancelScheduledInit() {
    if (this._idleCallbackId != null) {
      cancelIdleCallback(this._idleCallbackId);
      this._idleCallbackId = null;
    }
    if (this._timeoutId != null) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
    if (this._retryTimeoutId != null) {
      clearTimeout(this._retryTimeoutId);
      this._retryTimeoutId = null;
    }
    this._initScheduled = false;
  }

  scheduleInit() {
    if (this._initScheduled) return;
    this._initScheduled = true;

    const run = () => {
      this._initScheduled = false;
      this.init();
    };

    const isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;

    if (isDesignMode) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
      } else {
        run();
      }
      return;
    }

    const deferInit = () => {
      if ('requestIdleCallback' in window) {
        this._idleCallbackId = requestIdleCallback(run, { timeout: 2500 });
      } else {
        this._timeoutId = setTimeout(run, 1);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', deferInit, { once: true });
    } else {
      deferInit();
    }
  }

  getSwiperEl() {
    return this.querySelector('.home-slideshow-swiper');
  }

  updateAccessibility(swiper) {
    const slides = swiper && swiper.slides
      ? Array.from(swiper.slides)
      : Array.from(this.querySelectorAll('.swiper-slide'));
    const focusableSelector = 'a, button, input, textarea, select, details, [tabindex]';

    slides.forEach((slide) => {
      const isActive = slide.classList.contains('swiper-slide-active');
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.setAttribute('aria-current', isActive ? 'true' : 'false');

      if ('inert' in slide) {
        slide.inert = !isActive;
      } else if (isActive) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }

      slide.querySelectorAll(focusableSelector).forEach((element) => {
        if (isActive) {
          if (element.hasAttribute('data-a11y-original-tabindex')) {
            const originalTabindex = element.getAttribute('data-a11y-original-tabindex');
            if (originalTabindex === '') {
              element.removeAttribute('tabindex');
            } else {
              element.setAttribute('tabindex', originalTabindex);
            }
            element.removeAttribute('data-a11y-original-tabindex');
          }
          return;
        }

        if (!element.hasAttribute('data-a11y-original-tabindex')) {
          element.setAttribute(
            'data-a11y-original-tabindex',
            element.hasAttribute('tabindex') ? element.getAttribute('tabindex') : ''
          );
        }
        element.setAttribute('tabindex', '-1');
      });
    });
  }

  onSlideChangeTransitionEnd(swiper) {
    const $ = window.jQuery || window.$;
    if (!$) return;

    this.updateAccessibility(swiper);

    const next = $(this).find('.swiper-slide-active .text-wrapper').data('next');
    $(this).find('.next-text').text(next);

    const activeSlide = $(this).find('.swiper-slide-active');
    if (activeSlide.hasClass('white-header')) {
      $('.header-wrapper').addClass('white-header');
    } else {
      $('.header-wrapper').removeClass('white-header');
    }

    const position = activeSlide.attr('data-index');
    const bannerSlideName = activeSlide.attr('data-banner-name') || activeSlide.attr('aria-label').split(' ')[0];
    const bannerTitle = activeSlide.find('.text-btn-wrapper').attr('data-banner-title');

    if (!this._viewedBanners.includes(bannerSlideName)) {
      this._viewedBanners.push(bannerSlideName);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event_parameters: null });
      window.dataLayer.push({
        event: 'ga4Event',
        event_name: 'view_banner',
        event_parameters: {
          creative_slot: 'Home_Top_Banner',
          position: position,
          creative_name: bannerTitle,
        },
      });
    }

    if (activeSlide.data('color')) {
      activeSlide.parents('.home-slideshow-swiper').find('.next-text-wrapper').css('color', activeSlide.data('color'));
    } else {
      activeSlide.parents('.home-slideshow-swiper').find('.next-text-wrapper').css('color', '#ffffffcc');
    }
  }

  initExtras() {
    const $ = window.jQuery || window.$;
    if (!$) return;

    $(this)
      .find('.swiper-slide.has_video')
      .each(function () {
        const slide = this;
        const video = $(slide).find('video.hide-mb')[0];
        if (!video || !$(slide).find('.banner-img').length) return;
        video.removeEventListener('ended', video._homeSlideshowEndedHandler);
        video._homeSlideshowEndedHandler = function () {
          $(slide).find('.banner-video').addClass('hidden');
          $(slide).removeClass('has_video');
        };
        video.addEventListener('ended', video._homeSlideshowEndedHandler);
      });

    const floatingBox = this.querySelector('.floating-box');
    if (floatingBox) {
      const svgPath = $(floatingBox).find('svg path');
      const colors = [
        { bg: '#FCE605', svg: '#000' },
        { bg: '#F27642', svg: '#000' },
        { bg: '#E80E00', svg: '#fff' },
      ];
      const isMobile = window.innerWidth <= 900;
      const keyframes = isMobile
        ? [
            { x: 0, y: 0 },
            { x: 0, y: 42 },
            { x: 25, y: 6 },
          ]
        : [
            { x: 0, y: 0 },
            { x: 0, y: 105 },
            { x: 119, y: 32 },
          ];
      let currentFrame = 0;
      let nextFrame = 1;
      let startTime = null;
      const duration = 3000;

      const animate = () => {
        if (!floatingBox.isConnected) return;
        if (!startTime) startTime = performance.now();
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const startPos = keyframes[currentFrame];
        const endPos = keyframes[nextFrame];
        const x = startPos.x + (endPos.x - startPos.x) * progress;
        const y = startPos.y + (endPos.y - startPos.y) * progress;
        floatingBox.style.transform = 'translate(' + x + 'px, ' + y + 'px) rotate(10deg)';
        if (progress === 1) {
          currentFrame = nextFrame;
          nextFrame = (currentFrame + 1) % keyframes.length;
          startTime = null;
          const currentColor = colors[currentFrame % colors.length];
          floatingBox.style.backgroundColor = currentColor.bg;
          svgPath.css('fill', currentColor.svg);
        }
        requestAnimationFrame(animate);
      };

      setTimeout(() => requestAnimationFrame(animate), 1500);
    }

    const adjustThkRedLaunch = () => {
      const element = this.querySelector('.thk-red-launch .right-content');
      if (!element) return;
      const width = window.innerWidth;
      if (width >= 2000) {
        element.style.transform = 'scale(1) rotate(-11.84deg)';
        element.style.top = '160px';
      } else if (width >= 769) {
        const scale = 0.9 - (2000 - width) / 2400;
        element.style.transform = 'scale(' + scale + ') rotate(-11.84deg)';
        element.style.top = 180 + (2000 - width) / 15 + 'px';
      } else if (width <= 400) {
        element.style.transform = 'translate(-50%) scale(0.45) rotate(10deg)';
        element.style.top = '0px';
      } else {
        element.style.transform = 'translate(-50%) scale(0.55) rotate(10deg)';
        element.style.top = '0px';
      }
    };

    adjustThkRedLaunch();
    if (this._thkRedResizeHandler) {
      window.removeEventListener('resize', this._thkRedResizeHandler);
    }
    this._thkRedResizeHandler = adjustThkRedLaunch;
    window.addEventListener('resize', this._thkRedResizeHandler);
  }

  runCustomJs() {
    const configEl = this.querySelector('[data-home-slideshow-custom]');
    if (!configEl) return;

    let code = '';
    try {
      code = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('home-slideshow: invalid custom js config', e);
      return;
    }

    if (!code) return;

    const $ = window.jQuery || window.$;
    try {
      const fn = new Function('$', code);
      fn($);
    } catch (e) {
      console.error('home-slideshow: custom js error', e);
    }
  }

  bindSlideClicks() {
    const $ = window.jQuery || window.$;
    if (!$) return;

    $(this)
      .find('.swiper-slide')
      .off('click.homeSlideshow')
      .on('click.homeSlideshow', function () {
        const url = $(this).attr('data-url');
        if (url) location.href = url;
      });
  }

  init() {
    const swiperEl = this.getSwiperEl();
    if (!swiperEl) return;

    if (typeof Swiper === 'undefined') {
      this._retryTimeoutId = setTimeout(() => {
        this._retryTimeoutId = null;
        this.init();
      }, 100);
      return;
    }

    this.destroy();

    const autoplay = this.dataset.autoplay === 'true';
    const multiSlide = this.dataset.multiSlide === 'true';

    const swiperOptions = {
      touchRatio: 0.5,
      observer: true,
      observeParents: true,
      speed: 300,
      lazy: true,
      effect: 'fade',
      on: {
        init: (swiper) => this.updateAccessibility(swiper),
        slideChangeTransitionEnd: (swiper) => this.onSlideChangeTransitionEnd(swiper),
      },
    };

    if (autoplay) {
      swiperOptions.autoplay = {
        delay: 10000,
        disableOnInteraction: false,
      };
    }

    if (multiSlide) {
      swiperOptions.loop = true;
      swiperOptions.navigation = {
        nextEl: this.querySelector('.slideshow-button-next'),
        prevEl: this.querySelector('.slideshow-button-prev'),
      };
      swiperOptions.pagination = {
        el: this.querySelector('.slideshow-pagination'),
      };
    }

    this.swiper = new Swiper(swiperEl, swiperOptions);

    const $ = window.jQuery || window.$;
    if ($ && !multiSlide && $(this).find('.swiper-slide-active').hasClass('white-header')) {
      $('.header-wrapper').addClass('white-header');
    }

    this.bindSlideClicks();
    this.runCustomJs();
    this.initExtras();
  }

  destroy() {
    const swiperEl = this.getSwiperEl();
    if (swiperEl && swiperEl.swiper) {
      swiperEl.swiper.destroy(true, true);
    }
    if (this.swiper) {
      this.swiper = null;
    }

    if (this._thkRedResizeHandler) {
      window.removeEventListener('resize', this._thkRedResizeHandler);
      this._thkRedResizeHandler = null;
    }

    const $ = window.jQuery || window.$;
    if ($) {
      $(this).find('.swiper-slide').off('click.homeSlideshow');
    }

    this._viewedBanners = [];
  }
}

if (!customElements.get('home-slideshow')) {
  customElements.define('home-slideshow', HomeSlideshow);
}
